import {
  Children,
  useEffect,
  useContext,
  useRef,
  useState,
  useLayoutEffect,
  ReactNode,
} from "react";
import { ThemeContext } from "styled-components";
import { v4 as uuidv4 } from "uuid";

import usePreviousValue from "../usePreviousValue";

import ResponsiveLayout from "../ResponsiveLayout/ResponsiveLayout";
import NavButton from "./NavButton";
import Box from "../Box/Box";
import Pagination from "./Pagination";
import { HorizontalSliderProps } from "./horizontalSlider.type";
import ItemWrapper from "./ItemWrapper";
import SliderContainer from "./SliderContainer";
import Slider from "./Slider";

const HorizontalSlider = ({
  children,
  variant = "full-width",
  infinite = variant === "full-width" ? false : true,
  pagination = true,
  paginationPrimaryColor = "grey-lighten-50",
  paginationSecondaryColor = "grey-lighten-85",
  slidesToShow = variant === "full-width"
    ? {
        mobile: 1,
        tablet: 2,
        desktop: 3,
        desktopL: 4,
        default: 4,
      }
    : { mobile: 3, tablet: 3, desktop: 3, desktopL: 3, default: 3 },
  sliderLabel = "slider",
  width = "auto",
  arrows = true,
  swipe = true,
  draggable = true,
  onMouseEnter,
  onMouseLeave,
  onSlideChange = () => {},
  customArrows = [
    <NavButton direction="left" variant={variant} />,
    <NavButton direction="right" variant={variant} />,
  ],
  stageRightPadding = 0,
  stageMarginY = 0,
  stagePaddingY = 0,
  animationDelay = 0,
  activeSlideIndex = 0,
}: HorizontalSliderProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const sliderRef = useRef<any>(null);
  const themeContext = useContext(ThemeContext);

  const [maxSlideHeight, setMaxSlideHeight] = useState(0);
  const [uuid, setUUID] = useState("");

  const prevActiveSlideIndex = usePreviousValue(activeSlideIndex);

  useEffect(() => {
    if (!uuid) {
      setUUID(uuidv4());
    }
  }, [uuid]);

  useEffect(() => {
    const sliderNode = sliderRef.current;
    if (sliderNode) {
      const listNode = sliderNode.innerSlider.list;
      listNode.setAttribute("aria-label", sliderLabel);
    }
  }, [sliderLabel]);

  useLayoutEffect(() => {
    const timeout = setTimeout(() => {
      const sliderNode = sliderRef.current;
      if (sliderNode) {
        const currentMaxSlideHeight =
          sliderNode?.innerSlider?.state?.slideHeight;
        if (currentMaxSlideHeight && currentMaxSlideHeight !== maxSlideHeight) {
          setMaxSlideHeight(currentMaxSlideHeight);
          return;
        }
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [maxSlideHeight]);

  const getNumberFromBreakpoint = (breakpoint: string) =>
    Number(breakpoint.substr(0, breakpoint.length - 2));

  const sliderMobileBreakpoint = getNumberFromBreakpoint(
    themeContext.breakpoints["tablet"]
  );

  const sliderTabletBreakpoint = getNumberFromBreakpoint(
    themeContext.breakpoints["desktop"]
  );

  const getSlidesVisible = (currentBreakPoint: number | null) => {
    switch (currentBreakPoint) {
      case 415:
        return slidesToShow["mobile"];
      case 769:
        return slidesToShow["tablet"];
      case 1024:
        return slidesToShow["desktop"];
      case 1200:
        return slidesToShow["desktopL"];
      default:
        return slidesToShow["default"];
    }
  };

  const beforeChange = (current: number, next: number) => {
    const sliderNode = sliderRef.current;
    const currentBreakPoint = sliderNode?.state?.breakpoint || null;
    const slidesVisible = getSlidesVisible(currentBreakPoint);
    const currentPage = Math.ceil(next / slidesVisible);

    setCurrentPage(currentPage);
    onSlideChange(next);
  };

  const onReInit = () => {
    const currentMaxItemHeight =
      sliderRef.current?.innerSlider?.state?.slideHeight;
    if (maxSlideHeight > 0 && currentMaxItemHeight !== maxSlideHeight) {
      setMaxSlideHeight(currentMaxItemHeight);
    }
  };

  useEffect(() => {
    const sliderNode = sliderRef.current;
    const currentBreakPoint = sliderNode?.state?.breakpoint || null;
    const slidesVisible = getSlidesVisible(currentBreakPoint);
    const currentSlidePage = Math.floor(activeSlideIndex / slidesVisible);

    if (sliderNode && activeSlideIndex !== prevActiveSlideIndex) {
      sliderNode.slickGoTo(currentSlidePage * slidesVisible);
    }
  }, [activeSlideIndex, sliderRef, children]);

  const currentBreakPoint = sliderRef.current?.state?.breakpoint || null;
  const slidesVisible = getSlidesVisible(currentBreakPoint);

  const settings = {
    speed: 500,
    initialSlide: activeSlideIndex,
    infinite,
    dots: pagination,
    swipe,
    arrows,
    draggable,
    beforeChange,
    onReInit,
    ...(variant === "full-width" && {
      appendDots: (dots: ReactNode[]) => {
        return (
          <Pagination
            numberOfPages={dots.length}
            currentPage={currentPage}
            sliderNode={sliderRef.current}
            slidesVisible={slidesVisible}
            paginationPrimaryColor={paginationPrimaryColor}
            paginationSecondaryColor={paginationSecondaryColor}
            stageRightPadding={stageRightPadding}
          />
        );
      },
    }),
    prevArrow: customArrows[0] as any,
    nextArrow: customArrows[1] as any,
    slidesToScroll: slidesToShow.default,
    slidesToShow: slidesToShow.default,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: slidesToShow.desktopL,
          slidesToScroll: slidesToShow.desktopL,
        },
      },

      {
        breakpoint: 1024,
        settings: {
          slidesToShow: slidesToShow.desktop,
          slidesToScroll: slidesToShow.desktop,
        },
      },
      {
        breakpoint: sliderTabletBreakpoint,
        settings: {
          slidesToShow: slidesToShow.tablet,
          slidesToScroll: slidesToShow.tablet,
        },
      },
      {
        breakpoint: sliderMobileBreakpoint,
        settings: {
          slidesToShow: slidesToShow.mobile,
          slidesToScroll: variant === "dots" ? slidesToShow.mobile : 1,
        },
      },
    ],
  };

  return variant === "full-width" ? (
    <ResponsiveLayout marginY={stageMarginY}>
      <SliderContainer
        aria-label="slider container"
        tabIndex={0}
        width="calc(100% + 18px)"
        paddingLeft="18px"
        marginLeft="-18px"
        overflow="hidden"
        data-uuid={uuid}
      >
        <Slider
          ref={sliderRef}
          variant={variant}
          paddingRight={stageRightPadding}          
          {...settings}
          paddingY={stagePaddingY}          
        >
          {Children.map(children, (child, index) => (
            <div key={index}>
              <ItemWrapper
                animationDelay={animationDelay}
                maxSlideHeight={maxSlideHeight}
              >
                {child}
              </ItemWrapper>
            </div>
          ))}
        </Slider>
      </SliderContainer>
    </ResponsiveLayout>
  ) : (
    <Box width={width}>
      <SliderContainer
        aria-label="slider container"
        tabIndex={0}
        overflow="hidden"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-uuid={uuidv4()}
      >
        <Slider
          paddingRight={stageRightPadding}
          ref={sliderRef}
          variant={variant}
          {...settings}
        >
          {Children.map(children, (child, index) => (
            <div key={index}>{child}</div>
          ))}
        </Slider>
      </SliderContainer>
    </Box>
  );
};

export default HorizontalSlider;
